import { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useNavigate } from 'react-router-dom';
import EvaluationFilterForm from './EvaluationFilterForm';
import { Button, Table, Spin, Typography, Dropdown, Tooltip, Space, Tag } from 'antd';
import { fullPagerConfig } from '@/utils/property';
import { getEvalsPageApi } from '@services/Experts';
import { conferenceTypes } from '@utils/constants';
import { convertDateTimeRangeToLocalText } from '@utils/utils';
import { isEmpty } from 'lodash';
import EvaluationDrawer from './components/EvaluationDrawer';
import EvaluationCheckDrawer from './components/EvaluationCheckDrawer';
import { evaluationSliceActions } from './store/evaluationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { localStorageGet } from '@core/cookie';
import WorkExperiences from '@/components/common/WorkExperiences';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import './index.less';
import PageHeader from '@components/PageHeader/PageHeader';
const { Text, Link } = Typography;

const { setEvaluationFilter: setFilter, setEvaluationCursor: setCursor } = evaluationSliceActions;

const ExpertEvaluation = () => {
    const dispatch = useDispatch();
    const filter = useSelector(state => state.evaluation.evaluation.filter);
    const cursor = useSelector(state => state.evaluation.evaluation.cursor);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const navigate = useNavigate();

    // console.log(filter, 'init');

    // 渲染table数据的state
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [visibleEvaluationDrawer, setVisibleEvaluationDrawer] = useState(false);
    const [visibleEvaluationCheckDrawer, setVisibleEvaluationCheckDrawer] = useState(false);
    const [currentEvaluation, setCurrentEvaluation] = useState({}); // 当条专家综评信息

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 筛选数据
    const handleFilterFinish = values => {
        const newFilter = {
            ...filter,
            ...values,
            page: 1,
        };

        // console.log('filter', newFilter);

        Object.keys(newFilter).forEach(key => {
            if (!newFilter[key]) {
                delete newFilter[key];
            }
        });

        if (isEmpty(newFilter?.expertLevels)) {
            delete newFilter.expertLevels;
        }

        // console.log('newfilter', newFilter);
        dispatch(setFilter(newFilter));
    };

    // 排序下拉菜单
    const onClickDropdown = ({ key }) => {
        dispatch(setFilter({ ...filter, sortType: Number(key) + 1 }));
    };

    // 刷新
    const handleRefresh = () => {
        getExpertList();
    };

    const onChangePage = (page, pageSize) => {
        dispatch(setFilter({ ...filter, page, pageSize }));
    };

    // 编辑
    const toExpert = record => {
        // console.log('expert_id', row.id);
        dispatch(setCursor(record.id));
        navigate(`/dc/experts?detail=${record.expertId}`);
    };

    // 获取专家综评列表
    const getExpertList = async () => {
        setLoading(true);
        // console.log(filter, 'getexpertlist');
        const res = await getEvalsPageApi(filter);
        if (res && res.status_code === 200 && res.data) {
            const list = res.data.list || [];

            list.forEach((item, idx) => {
                item.id = idx + res.data.currPage * res.data.pageSize;

                if (!!item.remarks && item.remarks.length > 0) {
                    // 按时间倒序排序
                    item.remarks.sort((a, b) => {
                        return new Date(b.createdAt) - new Date(a.createdAt);
                    });
                }
            });

            // console.log(list, 'list');

            setTableData(list);
            setTotal(res.data.totalCount);
        }
        setLoading(false);
    };

    // 渲染table数据
    useEffect(() => {
        getExpertList();
    }, [filter]);

    // 是否拥有评价专家参会情况
    const canEvalExpert = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            const hasEvaluation = roleList.some(item => item.name === '评价专家参会情况');
            return hasEvaluation;
        }
        return false;
    }, []);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        size: 'small',
        total: total,
        showTotal: () => `共${total}条`,
        current: filter.page,
        pageSize: filter.pageSize,
        onChange: onChangePage,
    };

    // 切换特殊说明展开收起状态
    const toggleRemarksExpansion = record => {
        const recordKey = `${record.expertId}_${record.agendaId}`;
        const newTableData = tableData.map(item => {
            const itemKey = `${item.expertId}_${item.agendaId}`;
            if (itemKey === recordKey) {
                return { ...item, remarksExpanded: !item.remarksExpanded };
            }
            return item;
        });
        setTableData(newTableData);
    };

    // table 数据相关
    const columns = [
        {
            title: '任务',
            dataIndex: 'title',
            width: 300,
            render: (_, record) => (
                <>
                    <div>
                        {record.title}{' '}
                        <Tag color="blue" style={{ marginLeft: 4, marginBottom: 10 }}>
                            {record.taskName}
                        </Tag>
                    </div>
                    <div style={{ color: 'gray', fontSize: 12 }}>{convertDateTimeRangeToLocalText(record.start_time, record.end_time, 0)}</div>
                </>
            ),
        },
        {
            title: '记录人数',
            dataIndex: 'evaluationCount',
            width: 100,
            render: (_, record) =>
                !!record.evaluationCount ? (
                    <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => checkEvaluation(record)}>
                        {record.evaluationCount}人
                    </span>
                ) : (
                    <span style={{ color: 'gray' }}>暂无</span>
                ),
        },
        {
            title: '专家价值',
            dataIndex: 'totalScore',
            width: 150,
            render: (_, record) =>
                !!record.totalScore ? (
                    <>
                        <div>合作友好度：{record.friendlinessScore}分</div>
                        <div>报告专业度：{record.professionScore}分</div>
                        <div>价值度：{record.totalScore}分</div>
                    </>
                ) : (
                    <Text type="secondary">&ndash;</Text>
                ),
        },
        {
            title: '特殊说明',
            dataIndex: 'remarks',
            key: 'remarks',
            width: 300,
            render: (_, record) => {
                if (!!record.remarks && record.remarks.length > 0) {
                    return (
                        <div className="remarks">
                            {record.remarks.map((remark, idx) => {
                                return (
                                    <div className="remark" key={`${record.expertId}_${record.agendaId}_${idx}`} style={{ display: idx !== 0 && !record.remarksExpanded ? 'none' : '' }}>
                                        <div className="remark-extra">
                                            <span className="author">{remark.enterpriseName}</span>
                                            <span className="time">{remark.createdAt}</span>
                                        </div>
                                        <div className="remark-content">{remark.remark}</div>
                                    </div>
                                );
                            })}

                            {record.remarks.length > 1 && (
                                <div className="toggle-remarks-expansion" style={{ color: '#1890ff', cursor: 'pointer', fontSize: 12 }} onClick={() => toggleRemarksExpansion(record)}>
                                    <span className="anticon" style={{ transform: !!record?.remarksExpanded ? 'rotate(180deg)' : 'none' }}>
                                        <i className="bi bi-caret-down" style={{ fontSize: 12 }}></i>
                                    </span>
                                    <span>{!!record?.remarksExpanded ? '收起' : `展开${record.remarks.length}项`}</span>
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return <Text type="secondary">&ndash;</Text>;
                }
            },
        },
        {
            title: '基本信息',
            dataIndex: 'memUsername',
            key: 'memUsername',
            width: 200,
            render: (_, record) => <ExpertInfoCard expert={record.expertInfo} />,
        },
        {
            title: '单位职务',
            dataIndex: 'ssoMemberWorkExperiencesEntities',
            key: 'ssoMemberWorkExperiencesEntities',
            width: 260,
            render: (state, record) => <WorkExperiences workExperiences={record.expertInfo?.ssoMemberWorkExperiencesEntities} />,
        },
        {
            title: '项目名称',
            dataIndex: 'name',
            width: 260,
            render: (_, record) => (
                <>
                    <span>{record.name}</span>
                    {!!record.projectCode && (
                        <Tag color="volcano" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                            {record.projectCode}
                        </Tag>
                    )}
                    {!!record.type && (
                        <Tag color="volcano" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                            {conferenceTypes.find(item => item.type === record.type).label}
                        </Tag>
                    )}
                </>
            ),
        },
        {
            title: '项目执行',
            dataIndex: 'dates',
            width: 200,
            render: (_, record) => (
                <>
                    <div>日期：{record.dates?.replace(/,/g, ', ') || <Text type="secondary">&ndash;</Text>}</div>
                    {!!record.place && <div>地点：{record.place}</div>}
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'state',
            key: 'state',
            fixed: 'right',
            width: 100,
            align: 'center',
            hidden: !canEvalExpert,
            render: (_, record) =>
                !record.isEvaluated ? (
                    <Button type="link" onClick={() => handleEvaluation(record)}>
                        记录价值
                    </Button>
                ) : (
                    <Text type="secondary">已记录</Text>
                ),
        },
    ];

    // 记录价值
    const handleEvaluation = record => {
        // console.log('record', record);
        setCurrentEvaluation(record);
        setVisibleEvaluationDrawer(true);
        dispatch(setCursor(record.id));
    };

    // 查看记录
    const checkEvaluation = record => {
        // console.log('record', record);
        setCurrentEvaluation(record);
        setVisibleEvaluationCheckDrawer(true);
        dispatch(setCursor(record.id));
    };

    useEffect(() => {
        document.title = '专家价值-专家库';
    }, []);

    return (
        <div className="page-container list-page-container expert-eval-list-page">
            <PageHeader title="专家价值" breadcrumb={[{ label: '专家库', path: '/dc/experts' }, { label: '专家价值' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <EvaluationFilterForm handleFilterFinish={handleFilterFinish} filter={filter} setFilter={setFilter} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container dc-expert-eval-table-region">
                <div className="main-toolbar dc-expert-eval-toolbar">
                    <div className="main-tools">
                        <span className="table-title">专家价值列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Dropdown
                                menu={{
                                    items: [
                                        { key: 0, label: <>{filter?.sortType === 1 ? <Link>按任务时间倒序（默认）</Link> : '按任务时间倒序（默认）'}</> },
                                        { key: 1, label: <>{filter?.sortType === 2 ? <Link>按价值度从高到低</Link> : '按价值度从高到低'}</> },
                                        { key: 2, label: <>{filter?.sortType === 3 ? <Link>按合作友好度从高到低</Link> : '按合作友好度从高到低'}</> },
                                        { key: 3, label: <>{filter?.sortType === 4 ? <Link>按报告专业度从高到低</Link> : '按报告专业度从高到低'}</> },
                                    ],
                                    onClick: onClickDropdown,
                                }}
                            >
                                <Button
                                    type="default"
                                    className={filter?.sortType !== 1 ? 'sort-button-active' : ''}
                                    icon={
                                        <span className="anticon">
                                            <i className="bi bi-sort-down"></i>
                                        </span>
                                    }
                                    onClick={handleRefresh}
                                />
                            </Dropdown>
                            <Tooltip title="刷新列表">
                                <Button
                                    type="default"
                                    icon={
                                        <span className="anticon">
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </span>
                                    }
                                    onClick={handleRefresh}
                                />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap dc-expert-eval-table-wrap">
                    <Spin spinning={loading} delay={500}>
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            rowKey="id"
                            columns={columns.filter(item => !item.hidden)}
                            dataSource={tableData}
                            bordered
                            sticky
                            size="medium"
                            pagination={paginationProps}
                            rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                            scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        />
                    </Spin>
                </div>
            </div>

            <EvaluationDrawer visibleEvaluationDrawer={visibleEvaluationDrawer} setVisibleEvaluationDrawer={setVisibleEvaluationDrawer} currentEvaluation={currentEvaluation} getExpertEvaluationList={getExpertList} />

            <EvaluationCheckDrawer visibleEvaluationCheckDrawer={visibleEvaluationCheckDrawer} setVisibleEvaluationCheckDrawer={setVisibleEvaluationCheckDrawer} currentEvaluation={currentEvaluation} />
        </div>
    );
};

export default ExpertEvaluation;
